import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { SafetyPerformanceReview, PerformanceSnapshot } from '../types';
import { performanceService } from '../performanceService';
import { useDialog } from '../../../../shared/DialogContext';

export const SRBReviewsTab: React.FC = () => {
  const { showError, showSuccess } = useDialog();
  const [reviews, setReviews] = useState<SafetyPerformanceReview[]>([]);
  const [snapshots, setSnapshots] = useState<PerformanceSnapshot[]>([]);
  const [loading, setLoading] = useState(false);

  // New review form
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [periodReviewed, setPeriodReviewed] = useState('2026-Q1');
  const [chairperson, setChairperson] = useState('Accountable Manager');
  const [attendeesStr, setAttendeesStr] = useState('Safety Director, Chief Pilot, Head of Maintenance, Quality Manager');
  const [executiveSummary, setExecutiveSummary] = useState('');
  const [decisionsAndActions, setDecisionsAndActions] = useState('');

  // Lock snapshot form
  const [showSnapshotModal, setShowSnapshotModal] = useState(false);
  const [snapshotPeriod, setSnapshotPeriod] = useState('2026-03');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [revs, snaps] = await Promise.all([
        performanceService.getReviews(),
        performanceService.getSnapshots(),
      ]);
      setReviews(revs);
      setSnapshots(snaps);
    } catch (err) {
      console.error('Error loading SRB data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReview = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const attendees = attendeesStr
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      await performanceService.createReview({
        reviewDate: new Date().toISOString().split('T')[0],
        periodReviewed,
        chairperson,
        attendees,
        executiveSummary,
        decisionsAndActions,
        signedBy: chairperson,
      });
      setShowReviewModal(false);
      await loadData();
      showSuccess('Safety Performance Review successfully registered.');
    } catch (err) {
      showError(err, 'Failed to Save Safety Review');
    } finally {
      setLoading(false);
    }
  };

  const handleLockSnapshot = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await performanceService.createSnapshot({
        period: snapshotPeriod,
        lockedBy: 'SAFETY_DIRECTOR',
      });
      setShowSnapshotModal(false);
      await loadData();
      showSuccess(`Period ${snapshotPeriod} safety performance scorecard locked and archived.`);
    } catch (err) {
      showError(err, 'Failed to Lock Performance Snapshot');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Action Header */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            Safety Review Board (SRB) & Scorecard Snapshots
          </h2>
          <p className="text-sm text-gray-500">
            ICAO Annex 19 executive oversight meeting minutes, locked official scorecards, and management signoffs.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowSnapshotModal(true)}
            className="btn-secondary"
            style={{ fontSize: '12px', padding: '6px 12px' }}
          >
            🔒 Lock Monthly Snapshot
          </button>
          <button
            onClick={() => setShowReviewModal(true)}
            className="btn-primary"
            style={{ fontSize: '12px', padding: '6px 12px' }}
          >
            + Add SRB Meeting Record
          </button>
        </div>
      </div>

      {/* Snapshots Bar */}
      {snapshots.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-xs font-bold text-blue-900 uppercase tracking-wider mb-2">
            Locked & Archived Safety Scorecards (Snapshots)
          </h3>
          <div className="flex flex-wrap gap-2">
            {snapshots.map((snap) => (
              <div
                key={snap.id}
                className="bg-white px-3 py-1.5 rounded border border-blue-200 shadow-xs flex items-center gap-2 text-xs"
              >
                <span className="font-mono font-bold text-blue-700">📅 {snap.period}</span>
                <span className="text-gray-400">|</span>
                <span className="text-gray-600">Locked By: {snap.lockedBy}</span>
                <span className="text-[10px] text-gray-400">({new Date(snap.lockedAt).toLocaleDateString()})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SRB Reviews List */}
      <div className="space-y-4">
        <h3 className="text-base font-bold text-gray-800">Past SRB Meeting Minutes</h3>
        {!loading && reviews.length === 0 ? (
          <div className="p-8 text-center text-gray-500 bg-white rounded-lg border border-dashed border-gray-300">
            No SRB review records found.
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((rev) => (
              <div
                key={rev.id}
                className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm space-y-3"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-900 text-base">
                        SRB Review Period: {rev.periodReviewed}
                      </span>
                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800">
                        {rev.signoffStatus}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Date: {rev.reviewDate} | Chair: <span className="font-medium text-gray-700">{rev.chairperson}</span>
                    </div>
                  </div>
                  {rev.signedBy && (
                    <span className="text-xs bg-slate-100 text-slate-700 px-2.5 py-1 rounded font-mono">
                      Signed By: {rev.signedBy}
                    </span>
                  )}
                </div>

                <div className="bg-gray-50 p-3 rounded text-xs text-gray-700">
                  <span className="font-semibold text-gray-900">Attendees: </span>
                  {Array.isArray(rev.attendeesJson) ? rev.attendeesJson.join(', ') : rev.attendeesJson}
                </div>

                <div>
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    Executive Summary
                  </h4>
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {rev.executiveSummary}
                  </p>
                </div>

                {rev.decisionsAndActions && (
                  <div>
                    <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                      Decisions & Approved Actions
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {rev.decisionsAndActions}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review Modal */}
      {showReviewModal && (
        <div className="modal-backdrop">
          <div className="modal-card modal-lg">
            <div className="modal-header">
              <div className="modal-header-content">
                <h3>Record SRB Review Minutes</h3>
                <p>Safety Review Board executive minutes and actions</p>
              </div>
              <button type="button" onClick={() => setShowReviewModal(false)} className="modal-close-btn" title="Close" aria-label="Close">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateReview}>
              <div className="modal-body">
                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Reviewed Period</label>
                    <input
                      type="text"
                      required
                      value={periodReviewed}
                      onChange={(e) => setPeriodReviewed(e.target.value)}
                      placeholder="2026-Q1"
                    />
                  </div>
                  <div className="form-field">
                    <label>Chairperson</label>
                    <input
                      type="text"
                      required
                      value={chairperson}
                      onChange={(e) => setChairperson(e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label>Attendees (Comma-separated)</label>
                  <input
                    type="text"
                    required
                    value={attendeesStr}
                    onChange={(e) => setAttendeesStr(e.target.value)}
                  />
                </div>

                <div className="form-field">
                  <label>Executive Summary *</label>
                  <textarea
                    required
                    rows={3}
                    value={executiveSummary}
                    onChange={(e) => setExecutiveSummary(e.target.value)}
                    placeholder="Periodic safety performance evaluation summary..."
                  />
                </div>

                <div className="form-field">
                  <label>Decisions & Approved Actions</label>
                  <textarea
                    rows={2}
                    value={decisionsAndActions}
                    onChange={(e) => setDecisionsAndActions(e.target.value)}
                    placeholder="Key strategic actions agreed by the SRB..."
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowReviewModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? 'Saving...' : 'Approve & Save Minutes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Snapshot Modal */}
      {showSnapshotModal && (
        <div className="modal-backdrop">
          <div className="modal-card max-w-sm">
            <div className="modal-header">
              <div>
                <h3>Lock Scorecard Snapshot</h3>
                <p>Create immutable audit record for compliance</p>
              </div>
              <button type="button" onClick={() => setShowSnapshotModal(false)} className="close-btn" aria-label="Close modal">✕</button>
            </div>
            <form onSubmit={handleLockSnapshot}>
              <div className="modal-body">
                <p style={{ fontSize: '12px', color: 'var(--text-muted, #64748b)', margin: '0 0 12px 0' }}>
                  All SPI indicators and scores for this period will be locked as an immutable audit snapshot.
                </p>
                <div className="form-field">
                  <label>Lock Period (YYYY-MM)</label>
                  <input
                    type="text"
                    required
                    value={snapshotPeriod}
                    onChange={(e) => setSnapshotPeriod(e.target.value)}
                    placeholder="2026-03"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowSnapshotModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? 'Locking...' : 'Lock Snapshot'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
